import { useEffect, useRef } from 'react'

import { Location } from '@/features/location'
import { RoomConfigurationType, validateRoomConfiguration } from '@/features/room-config'
import { useAppConfigStore } from '@/stores/app-store-config/appConfigStore'
import { useCartStore } from '@/stores/cart-store/cartStore'
import { useStepStore } from '@/stores/step-store/stepStore'

import { RoomConfigQuery } from './useUrlQuery'

const mapRoomConfigQuery = (rcq: NonNullable<RoomConfigQuery>, location: Location) =>
	rcq.map((rc) => ({
		adults: rc.adults,
		children: rc.children ?? [],
		type: rc.type ?? undefined,
		roomSuggestion: {
			roomTypeCode: rc.roomSuggestion?.roomTypeCode ?? undefined,
			ratePlanCode: rc.roomSuggestion?.ratePlanCode ?? undefined,
			serviceCodes: rc.roomSuggestion?.serviceCodes ?? [],
		},
		isValid: validateRoomConfiguration(rc.adults, rc.children ?? [], location.roomTypes),
	}))

export const useRoomConfigurationQuery = () => {
	const urlQuery = useAppConfigStore.use.urlQuery()
	const setUrlQuery = useAppConfigStore.use.setUrlQuery()
	const setRoomConfigurations = useCartStore.use.setRoomConfigurations()
	const setNextStep = useStepStore.use.setNextStep()
	const location = useCartStore.use.location()

	const hasRun = useRef(false)

	useEffect(() => {
		if (hasRun.current) return
		if (!urlQuery) return
		if (!location) return

		const { roomConfigs, ...restQuery } = urlQuery
		if (!roomConfigs || roomConfigs.length === 0) return

		const mappedConfigs = mapRoomConfigQuery(roomConfigs, location)
		setRoomConfigurations(mappedConfigs)

		const isAllRoomsValid = mappedConfigs.every((room) => room.isValid)

		if (isAllRoomsValid) {
			setNextStep()
		}

		hasRun.current = true
		setUrlQuery(restQuery)
	}, [setNextStep, urlQuery, setRoomConfigurations, setUrlQuery, location])
}
